/**
 * FindingCard — one daemon review finding with links to the run evidence and the daemon's findings. Demo page:
 * /ui-elements/alerts (a titled note with severity).
 */
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge } from '../../../components/ui/index.js';
import { daemonRunPath } from '../catalogReviewLinks.js';

export function FindingCard({
  finding,
  nextStepLabel,
}: {
  finding: {
    id: number;
    daemonId?: number;
    workflowRunId?: number | null;
    severity: string;
    title: string;
    description?: string | null;
    recommendation?: string | null;
    affectedFiles?: string[];
    affectedSymbols?: string[];
  };
  nextStepLabel: string;
}) {
  return (
    <div className="rounded-sm border bg-surface-alt p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={
            finding.severity === 'critical' || finding.severity === 'high'
              ? 'danger'
              : finding.severity === 'medium'
                ? 'warning'
                : 'neutral'
          }
          size="sm"
        >
          {finding.severity}
        </Badge>
        <span className="font-medium text-heading">{finding.title}</span>
      </div>
      {finding.description && <p className="text-sm text-body mt-2">{finding.description}</p>}
      {finding.recommendation && (
        <p className="text-sm text-muted mt-2">
          {nextStepLabel}: {finding.recommendation}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {finding.daemonId && finding.workflowRunId && (
          <Link
            to={daemonRunPath(finding.daemonId, finding.workflowRunId)}
            className="inline-flex items-center gap-1 text-accent-text hover:underline"
          >
            Open run evidence <ChevronRight size={13} />
          </Link>
        )}
        {finding.daemonId && (
          <Link
            to={`/admin/daemons/${finding.daemonId}?tab=findings`}
            className="inline-flex items-center gap-1 text-accent-text hover:underline"
          >
            Open daemon findings <ChevronRight size={13} />
          </Link>
        )}
        {(finding.affectedFiles?.length ?? 0) > 0 && (
          <span className="text-muted">{finding.affectedFiles!.length} file refs</span>
        )}
        {(finding.affectedSymbols?.length ?? 0) > 0 && (
          <span className="text-muted">{finding.affectedSymbols!.length} symbol refs</span>
        )}
      </div>
    </div>
  );
}
